import { forwardRef } from "react";
import { PassThroughPropsWithRef } from "../../types";
import cx from "classnames";

import { useDrillDownContext } from "./internal/useDrillDownContext";
import { useTrackingId } from "../../hooks";
import { childrenToString } from "../../internal/functions";

import { Flex } from "../Flex";
import { Button } from "../Button";
import { Text, type TextProps } from "../Text";

import styles from "./DrillDown.module.scss";
import IconArrowBackIos from "@servicetitan/hammer-icon/mdi/round/arrow_back_ios.svg";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";

/**
 * Props for the DrillDownHeader component.
 *
 * @property el - HTML element tag for the header text. Defaults to "h1".
 *
 * @extends PassThroughPropsWithRef<"header">
 */
export type DrillDownHeaderProps = PassThroughPropsWithRef<"header"> & {
  el?: TextProps["el"];
};

/**
 * Header section for DrillDown screens with navigation controls.
 *
 * Displays a back button, title text, and optional close button. The back button navigates
 * to the previous drilldown index. The close button appears when the parent DrillDown has
 * an onClose prop.
 *
 * Features:
 * - Automatic back button with previous navigation
 * - Optional close button when onClose is provided
 * - Automatic analytics tracking via data-tracking-id
 * - Semantic heading with configurable element tag
 * - Accessible button labels for screen readers
 *
 * @example
 * ```tsx
 * <DrillDown.Header>Settings</DrillDown.Header>
 * <DrillDown.Header el="h2">Advanced Options</DrillDown.Header>
 * ```
 */
export const DrillDownHeader = forwardRef<HTMLDivElement, DrillDownHeaderProps>(
  (props, ref) => {
    const { el, className, children, ...rest } = props;

    const { index, back, onClose } = useDrillDownContext();

    const data = {
      children: childrenToString(props.children),
    };
    const trackingId = useTrackingId({
      name: "DrillDownHeader",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    return (
      <header
        {...rest}
        className={cx(styles["header"], className)}
        data-tracking-id={trackingId}
        ref={ref}
      >
        <Flex justifyContent="space-between">
          <Flex gap="4">
            <span className={styles["close-container"]}>
              <Button
                size="small"
                appearance="ghost"
                aria-label="Previous"
                data-anv="drilldown-header-back-button"
                icon={IconArrowBackIos}
                onClick={() => back?.()}
              />
            </span>

            <Text variant="headline" el={el ?? "h1"}>
              {children}
            </Text>
          </Flex>

          {index != null && onClose && onClose[index] ? (
            <span className={styles["close-container"]}>
              <Button
                className={styles["close-button"]}
                size="small"
                appearance="ghost"
                aria-label="Close"
                data-anv="drilldown-header-close-button"
                icon={Close}
                onClick={onClose[index]}
              />
            </span>
          ) : null}
        </Flex>
      </header>
    );
  },
);

DrillDownHeader.displayName = "DrillDownHeader";
